'use client';

import { CacheKeys, ServerActionName } from '@headless-workspace/config';
import {
    ResultEmpty,
    ResultServerAction,
    ResultServerActionType,
    ResultSuccess,
    unwrapSettledResultSuccess,
} from '@headless-workspace/core-domain';
import { getFavoriteStoreServerAction, getSelectedStoreServerAction, StoreInfoValue } from '../../../../server';
import { useSafeSWR } from '../../hooks';

type SelectedAndFavoriteStores = {
    selectedStore?: StoreInfoValue;
    favoriteStore?: StoreInfoValue;
};

type UseSelectedStoreArgs = {
    productId?: string;
    onSuccess: ({ selectedStore, favoriteStore }: SelectedAndFavoriteStores) => void;
};

type UseSelectedStoreReturnType = {
    isLoading: boolean;
};

const fetchSelectedAndFavoriteStores = async (
    productId?: string,
): Promise<ResultServerActionType<SelectedAndFavoriteStores>> => {
    const results = await Promise.allSettled([
        productId
            ? getSelectedStoreServerAction(productId)
            : Promise.reject(ResultServerAction(ResultEmpty(), ServerActionName.getSelectedStore)),
        getFavoriteStoreServerAction(),
    ]);

    const selectedStore = unwrapSettledResultSuccess(results[0]);
    const favoriteStore = unwrapSettledResultSuccess(results[1]);

    return ResultServerAction(ResultSuccess({ selectedStore, favoriteStore }), ServerActionName.getSelectedStore);
};

export const useSelectedStore = ({ productId, onSuccess }: UseSelectedStoreArgs): UseSelectedStoreReturnType => {
    const { isLoading } = useSafeSWR(
        productId ? [CacheKeys.SelectedStore, productId, CacheKeys.FavoriteStore] : CacheKeys.FavoriteStore,
        ServerActionName.getSelectedStore,
        () => fetchSelectedAndFavoriteStores(productId),
        {
            onSuccess: (successData: ResultServerActionType<SelectedAndFavoriteStores>) => {
                if (successData.type === 'success') {
                    onSuccess({
                        selectedStore: successData.data.selectedStore,
                        favoriteStore: successData.data.favoriteStore,
                    });
                }
            },
        },
    );

    return { isLoading };
};
